import { bemClassBuilder } from '@vista-digital/bem-class-builder'
import { useI18n } from '@vista-digital/occ-ui/src/lib/context'
import { useOccAccessibilityText } from '@vista-digital/occ-ui/src/lib/context/occ/hooks/use-occ-accessibility-text'
import { millisecondsToSeconds } from 'date-fns'
import { motion, useAnimationControls } from 'motion/react'
import { useEffect } from 'react'

interface CarouselPositionIndicatorProps {
    selected: boolean
    index: number
    onClick: VoidFunction
    autoPlay?: {
        enabled: boolean
        delay?: number
        isPlaying: boolean
    }
}

const b = bemClassBuilder('v-carousel-position-indicator')

export const CarouselPositionIndicator: React.FC<CarouselPositionIndicatorProps> = props => {
    const { selected, index, onClick, autoPlay } = props
    const i18n = useI18n()
    const { carouselPositionIndicatorAriaLabel } = useOccAccessibilityText()
    const controls = useAnimationControls()

    useEffect(() => {
        if (autoPlay?.isPlaying) {
            void controls.start({
                width: '40px',
                transition: { duration: millisecondsToSeconds(autoPlay.delay || 4000) },
            })
        } else {
            controls.stop()
            controls.set({ width: '10px' })
        }
    }, [autoPlay, controls])

    const showAutoPlayProgress = autoPlay?.enabled && selected

    return (
        <button
            className={b.block({ selected, autoPlayActive: showAutoPlayProgress })}
            onClick={onClick}
            tabIndex={-1}
            aria-selected={selected}
            aria-label={i18n.template(carouselPositionIndicatorAriaLabel, { slideNumber: index + 1 })}
            type="button"
            role="tab"
        >
            {showAutoPlayProgress && <motion.div className={b.element('progress-bar')} animate={controls} />}
        </button>
    )
}

CarouselPositionIndicator.displayName = 'CarouselPositionIndicator'
